import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/ui-bits";
import { BRAND } from "@/data/site";

const PROMISES = [
  {
    h: "Clinically led",
    p: "Appropriate assessment, confirmation of diagnosis and individualised treatment decisions. Every page of clinical content is written against current UK guidance and reviewed before launch.",
  },
  {
    h: "Transparent",
    p: "What is included, what is excluded and what additional investigations may cost — published, not negotiated. The full expected cost of the first 12 months, not just the headline.",
  },
  {
    h: "Accessible",
    p: "Online consultations, convenient blood testing and discreet delivery where clinically appropriate — with the same clinical standard as a physical clinic.",
  },
  {
    h: "Long-term",
    p: "Monitoring, prescription management, side-effect review and clear escalation pathways. Treatment is the start of a relationship, not a delivery service.",
  },
  {
    h: "Independent advice",
    p: "We explain when TRT may not be appropriate, discuss fertility implications and alternatives, and tell you when the right next step lies outside our service.",
  },
];

const COMPARISON = [
  { name: "Voy", advertised: "TRT subscriptions from around £99/month", note: "We compete on clinical education and simpler inclusions." },
  { name: "Numan", advertised: "Advertised plans from £89/month", note: "We compete on transparency of suitability and the complete pathway." },
  { name: "Karma Health", advertised: "Base £120 · Thrive £150 · Ascend £250/month", note: "We compete with a premium experience that keeps advanced extras optional." },
  { name: "Leger Clinic", advertised: "Established specialist clinic", note: "We compete on digital convenience, content depth and a purpose-built patient experience." },
];

export default function WhyPage() {
  return (
    <>
      <Seo
        title="Why Androgen Health | Specialist TRT Care, UK"
        description="Specialist care. Clear costs. No pressure. How Androgen Health compares with the established UK online TRT clinics — and the promises we hold ourselves to."
      />
      <PageHero
        label="Why Androgen Health"
        title={
          <>
            {BRAND.positioning.split(" ").slice(0, 2).join(" ")}{" "}
            <span className="serif-it text-teal">{BRAND.positioning.split(" ").slice(2, 3).join(" ")}</span>{" "}
            {BRAND.positioning.split(" ").slice(3).join(" ")}
          </>
        }
        lede="The strongest opportunity in UK testosterone health is not another website selling TRT. It is becoming the most trusted and useful resource for it — and letting the medicine do the selling."
        safety={false}
      />

      <section className="pb-20 px-5 sm:px-8" data-testid="why-body">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Label>The five promises</Label>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {PROMISES.map((p, i) => (
              <Reveal key={p.h} delay={i * 0.06} className="h-full">
                <div className="card-line rounded-2xl p-8 h-full">
                  <h2 className="font-display text-2xl sm:text-3xl tracking-wide text-teal">{p.h}</h2>
                  <p className="text-mute text-sm leading-relaxed mt-3">{p.p}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="h-full">
              <div className="card-line rounded-2xl p-8 h-full flex flex-col justify-between bg-elevated/60">
                <p className="font-display text-2xl sm:text-3xl tracking-wide leading-tight">
                  The aim is not to be the cheapest. It is to be the one with nothing to hide.
                </p>
                <Link to="/trt-cost" className="text-teal text-sm mt-6 link-underline self-start" data-testid="why-fees-link">
                  See the fees, in full
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="mt-24 max-w-3xl">
            <Reveal>
              <Label>The landscape</Label>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-wide mt-5">
                How we compare
              </h2>
              <p className="text-mute text-base leading-relaxed mt-4">
                Published, advertised prices of the established UK players — shown as advertised,
                not as a like-for-like comparison of every patient's total cost. Our own proposed
                launch pricing is £99–£179/month across the three plans.
              </p>
            </Reveal>
          </div>
          <div className="mt-10 space-y-4">
            {COMPARISON.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.05}>
                <div className="card-line rounded-2xl p-6 sm:p-7 grid sm:grid-cols-12 gap-4 items-baseline" data-testid={`compare-row-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className="font-display text-2xl tracking-wide sm:col-span-3">{c.name}</p>
                  <p className="font-mono text-xs text-dusk sm:col-span-4">{c.advertised}</p>
                  <p className="text-mute text-sm leading-relaxed sm:col-span-5">{c.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="text-xs text-dusk mt-6 max-w-3xl leading-relaxed">
              Competitor prices as publicly advertised and cited in our market review; they may
              change without notice and are not a like-for-like comparison. We recommend validating
              any clinic — including ours — on clinical governance before price.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
